import { ArrowRight, ImageOff } from 'lucide-react'
import { useState } from 'react'
import { toBuyLinkHref } from '~/src/lib/withRedirectClickSource'
import { useHubAnalytics } from '../HubAnalyticsContext'
import type {
  HubProductRailSection,
  HubProductRef,
  HubSectionComponentProps,
} from './types'

function RailCard({
  product,
  position,
}: {
  product: HubProductRef
  position: number
}) {
  const [errored, setErrored] = useState(false)
  const hubAnalytics = useHubAnalytics()
  const firstBuyLink = product.buyLinks[0]

  return (
    <article className='flex min-w-0 flex-col gap-3 rounded-2xl border-[0.5px] border-border bg-white p-4'>
      <div className='relative aspect-square overflow-hidden rounded-xl border-[0.5px] border-border bg-white'>
        {product.image && !errored ? (
          <img
            src={product.image}
            alt={product.name}
            className='h-full w-full object-contain'
            loading='lazy'
            onError={() => setErrored(true)}
          />
        ) : (
          <div className='flex h-full w-full items-center justify-center bg-ds-bg-secondary'>
            <ImageOff
              className='size-8 text-ds-fg-quaternary'
              strokeWidth={1.5}
            />
          </div>
        )}
      </div>

      <div className='flex min-w-0 flex-col gap-1'>
        {product.brand && (
          <p className='text-xs font-medium text-foreground-tertiary'>
            {product.brand}
          </p>
        )}
        <h3 className='line-clamp-2 text-balance text-sm font-semibold leading-5 text-foreground'>
          {product.name}
        </h3>
        {product.summary && (
          <p className='line-clamp-2 text-xs leading-5 text-foreground-secondary'>
            {product.summary}
          </p>
        )}
      </div>

      {firstBuyLink && (
        <a
          href={toBuyLinkHref(firstBuyLink.url, 'seo-hub')}
          target='_blank'
          rel='sponsored nofollow noopener noreferrer'
          className='mt-auto inline-flex items-center gap-1 text-sm font-semibold text-info-700 hover:underline'
          onClick={() =>
            hubAnalytics?.onProductClick({
              productId: product.productId,
              productSlug: product.slug,
              productName: product.name,
              outboundUrl: firstBuyLink.url,
              price: firstBuyLink.price ?? null,
              position,
              sectionType: 'productRail',
            })
          }
        >
          Where to buy
          <ArrowRight className='size-3.5' aria-hidden />
        </a>
      )}
    </article>
  )
}

export function HubProductRail({
  section,
  context,
}: HubSectionComponentProps<HubProductRailSection>) {
  const products = section.productIds
    .map((id) => context.productsById[id])
    .filter((p): p is HubProductRef => p != null)

  if (products.length === 0) return null

  return (
    <section id={section.id} className='flex min-w-0 flex-col gap-4'>
      {section.title && (
        <h2 className='font-display text-2xl font-bold leading-8 text-foreground'>
          {section.title}
        </h2>
      )}
      {section.dek && (
        <p className='text-base leading-6 text-foreground-secondary'>
          {section.dek}
        </p>
      )}

      <div
        className='grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 md:gap-4 lg:grid-cols-3 xl:grid-cols-4'
        role='region'
        aria-label={section.title ?? 'Product rail'}
      >
        {products.map((product, index) => (
          <RailCard
            key={product.productId}
            product={product}
            position={index}
          />
        ))}
      </div>
    </section>
  )
}
